import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

import { RequirePermission, RequireSection } from '@/shared/routing';
import { EMPLOYEE_DIRECTORY_PERMISSION, UsersPage } from '@/features/employees';

import { UsersLayout } from './ui/UsersLayout';
import { AccessMatrixPage } from './ui/pages/AccessMatrixPage';
import { AccessReviewPage } from './ui/pages/AccessReviewPage';
import { EffectiveAccessPage } from './ui/pages/EffectiveAccessPage';
import { GroupsPage } from './ui/pages/GroupsPage';

/**
 * The Users workspace: staff, groups, access and self-service, under one tabbed shell.
 *
 * <p>Mounted at `/team` rather than a path matching its label, so existing links, bookmarks and the
 * sidebar entry keep working — the section is renamed in the UI, not in the URL space.
 *
 * <p>Three tiers of access, not one (plan-auth-001 Phase 3.2):
 *
 * <ul>
 *   <li>The **staff directory** needs only `employees.view` — seeing who works here is not the same
 *       privilege as administering them, and the whole subtree used to sit behind `employees.manage`, so
 *       an operations manager could not look up a colleague's role without also being able to remove
 *       them. (`employees.manage` still opens it: the server accepts either, and so does this guard.)</li>
 *   <li>**Groups, access and review** stay on `employees.manage` — they grant and revoke rights.</li>
 *   <li>**`my-access`** sits outside the permission guard on purpose: every staff member may inspect
 *       their own resolved access, including the ones with no administrative rights at all.</li>
 * </ul>
 */
export const usersRoutes: RouteObject = {
  path: 'team',
  element: <UsersLayout />,
  children: [
    {
      // The `team` section guard sits here rather than on the layout, so it covers the administration
      // screens without also covering `my-access` below. Without it the workspace was the one section
      // whose sidebar feature flag was never enforced at the URL — hidden from the menu, still served
      // to anyone with the link, which is precisely the shape adr-merchant-portal-017 exists to remove.
      element: <RequireSection section="team" />,
      children: [
        {
          element: <RequirePermission permission={EMPLOYEE_DIRECTORY_PERMISSION} />,
          children: [{ index: true, element: <UsersPage /> }],
        },
        {
          element: <RequirePermission permission="employees.manage" />,
          children: [
            { path: 'groups', element: <GroupsPage /> },
            { path: 'access', element: <AccessMatrixPage /> },
            { path: 'review', element: <AccessReviewPage /> },
          ],
        },
      ],
    },
    { path: 'my-access', element: <EffectiveAccessPage /> },
    { path: '*', element: <Navigate to="/team" replace /> },
  ],
};
